/**
 * InlineCountForm
 * 
 * Compact count entry form for use in popups and inline contexts.
 * Shows supply and/or demand fields based on supplyMode.
 * 
 * LAT-25: below_zero validation inline (shows error with current count)
 * LAT-26: double-submit prevention; on failure form stays populated
 * 
 * Used by: N10 (trucker count), N11 (desktop count from marker), N12 (inventory inline edit)
 * 
 * Props:
 *   supplyMode: 'supply_only' | 'demand_only' | 'both'
 *   onSubmit: ({ supplyDelta, demandDelta, idempotencyKey }) => Promise
 *   loading: boolean
 *   error: string | null
 *   currentCounts: { supplyManual, demandManual } (for below_zero validation display)
 *   initialValues: { supplyDelta?, demandDelta? }
 *   compact: boolean (default: false — for popup contexts)
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { validateBelowZero } from '../../utils/countModel';

// Generate a unique idempotency key per form submission
function generateIdempotencyKey() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export default function InlineCountForm({
  supplyMode = 'both',
  onSubmit,
  loading = false,
  error = null,
  currentCounts = { supplyManual: 0, demandManual: 0 },
  initialValues = {},
  compact = false,
}) {
  const theme = useTheme();
  const [supplyDelta, setSupplyDelta] = useState(initialValues.supplyDelta ?? '');
  const [demandDelta, setDemandDelta] = useState(initialValues.demandDelta ?? '');
  const [validationError, setValidationError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const idempotencyKeyRef = useRef(generateIdempotencyKey());

  const showSupply = supplyMode === 'supply_only' || supplyMode === 'both';
  const showDemand = supplyMode === 'demand_only' || supplyMode === 'both';

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setValidationError(null);

    const supply = showSupply ? parseInt(supplyDelta, 10) || 0 : 0;
    const demand = showDemand ? parseInt(demandDelta, 10) || 0 : 0;

    if (supply === 0 && demand === 0) {
      setValidationError('Enter at least one non-zero value');
      return;
    }

    // LAT-25: below_zero validation — names the current count
    if (supply < 0) {
      const check = validateBelowZero(currentCounts.supplyManual, supply);
      if (!check.valid) {
        setValidationError(
          `Cannot reduce supply below zero. Current manual supply count is ${check.currentCount}.`
        );
        return;
      }
    }

    if (demand < 0) {
      const check = validateBelowZero(currentCounts.demandManual, demand);
      if (!check.valid) {
        setValidationError(
          `Cannot reduce demand below zero. Current manual demand count is ${check.currentCount}.`
        );
        return;
      }
    }

    // LAT-26: disable submit on click (loading prop), re-enable on response
    setSubmitted(true);
    try {
      await onSubmit({
        supplyDelta: supply,
        demandDelta: demand,
        idempotencyKey: idempotencyKeyRef.current,
      });
      // Reset form on success
      setSupplyDelta('');
      setDemandDelta('');
      idempotencyKeyRef.current = generateIdempotencyKey();
      setSubmitted(false);
    } catch (err) {
      // LAT-26: on failure, form stays populated (losing nothing)
      setSubmitted(false);
    }
  }, [supplyDelta, demandDelta, showSupply, showDemand, currentCounts, onSubmit]);

  const isDisabled = loading || submitted;
  const spacing = compact ? 1 : 2;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing,
        p: compact ? 1 : 2,
      }}
    >
      {/* Supply field */}
      {showSupply && (
        <TextField
          label="Supply Δ"
          type="number"
          variant="outlined"
          size="small"
          value={supplyDelta}
          onChange={(e) => setSupplyDelta(e.target.value)}
          disabled={isDisabled}
          inputProps={{ step: 1 }}
          fullWidth
          helperText={
            currentCounts.supplyManual !== undefined
              ? `Current: ${currentCounts.supplyManual}`
              : undefined
          }
        />
      )}

      {/* Demand field */}
      {showDemand && (
        <TextField
          label="Demand Δ"
          type="number"
          variant="outlined"
          size="small"
          value={demandDelta}
          onChange={(e) => setDemandDelta(e.target.value)}
          disabled={isDisabled}
          inputProps={{ step: 1 }}
          fullWidth
          helperText={
            currentCounts.demandManual !== undefined
              ? `Current: ${currentCounts.demandManual}`
              : undefined
          }
        />
      )}

      {/* Validation error */}
      {validationError && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          <Typography variant="body2">{validationError}</Typography>
        </Alert>
      )}

      {/* API error */}
      {error && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}

      {/* Submit button — LAT-26: disabled after click, re-enabled on response */}
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={isDisabled}
        fullWidth
        sx={{
          minHeight: compact ? theme.spacing(11) : theme.spacing(14),
          textTransform: 'none',
          fontWeight: 600,
        }}
      >
        {isDisabled ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          'Submit Count'
        )}
      </Button>
    </Box>
  );
}
